import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Table,
  Input,
  Button,
  Label,
} from "reactstrap";
import { Link } from "react-router-dom";
import { useFirebase } from "model/context/firebase.context";
import { COLLECTIONS } from "model/constants";
import { ProjectTicketModal } from "components/Project/ProjectTicketModal";

const TicketsOverview = () => {
  const [projectTickets, setProjectTickets] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [instructionsForFilter, setInstructionsForFilter] = useState("");
  const [expandedProjects, setExpandedProjects] = useState({});
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editTicket, setEditTicket] = useState(null);
  const [allExpanded, setAllExpanded] = useState(false);

  const { db } = useFirebase();

  useEffect(() => {
    const unsubscribeProjects = db.listen(COLLECTIONS.projects, (data) => {
      if (!data) {
        setProjectTickets({});
        return;
      }

      const newProjectTickets = {};

      for (const [projectId, projectValue] of Object.entries(data)) {
        if (!projectValue || !projectValue.tickets) continue;

        const projectName = projectValue.name || "Unknown Project";
        const ticketsArray = Object.entries(projectValue.tickets).map(
          ([ticketId, ticketValue]) => ({
            ticketId,
            projectId,
            projectName,
            ...ticketValue,
          })
        );

        newProjectTickets[projectId] = {
          projectName,
          tickets: ticketsArray,
        };
      }

      setProjectTickets(newProjectTickets);
    });

    return () => unsubscribeProjects();
  }, [db]);

  const handleToggleExpand = (projectId) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const handleToggleChecked = async (ticket) => {
    const updated = { ...ticket, checked: !ticket.checked };

    setProjectTickets((prev) => {
      const next = { ...prev };
      const project = next[ticket.projectId];
      if (!project) return prev;

      const idx = project.tickets.findIndex((t) => t.ticketId === ticket.ticketId);
      if (idx === -1) return prev;

      const updatedTickets = [...project.tickets];
      updatedTickets[idx] = updated;
      next[ticket.projectId] = { ...project, tickets: updatedTickets };
      return next;
    });

    try {
      const projectSnap = await db.get(COLLECTIONS.projects, ticket.projectId);
      if (!projectSnap) throw new Error("Project not found");

      const projectPlain = JSON.parse(JSON.stringify(projectSnap));
      if (!projectPlain.tickets) projectPlain.tickets = {};

      projectPlain.tickets[ticket.ticketId] = {
        ...(projectPlain.tickets[ticket.ticketId] || {}),
        ...updated,
      };

      await db.update(COLLECTIONS.projects, ticket.projectId, projectPlain);
    } catch (e) {
      console.error("Failed to update ticket:", e);
      setError(e?.message || "Could not update the ticket");
    }
  };

  const handleEditTicket = (ticket) => {
    setEditTicket({ ...ticket, id: ticket.ticketId });
    setModalOpen(true);
  };

  const toggleExpandAll = () => {
    const newState = !allExpanded;
    const newExpandedProjects = {};
  
    Object.keys(projectTickets).forEach((projectId) => {
      newExpandedProjects[projectId] = newState;
    });
  
    setExpandedProjects(newExpandedProjects);
    setAllExpanded(newState);
  };
  

  const handleDeleteTicket = async (ticket) => {
    try {
      setProjectTickets((prev) => {
        const next = { ...prev };
        const project = next[ticket.projectId];
        if (!project) return prev;

        project.tickets = project.tickets.filter((t) => t.ticketId !== ticket.ticketId);
        next[ticket.projectId] = project;
        return next;
      });

      const projectSnap = await db.get(COLLECTIONS.projects, ticket.projectId);
      if (!projectSnap) throw new Error("Project not found");

      const projectPlain = JSON.parse(JSON.stringify(projectSnap));
      if (projectPlain.tickets && projectPlain.tickets[ticket.ticketId]) {
        delete projectPlain.tickets[ticket.ticketId];
      }

      await db.update(COLLECTIONS.projects, ticket.projectId, projectPlain);
    } catch (e) {
      console.error("Failed to delete ticket:", e);
      setError(e.message || "Could not delete the ticket");
    }
  };

  const renderControlPanel = () => (
<div className="d-flex flex-wrap align-items-center justify-content-start gap-4 mb-4">

 {/* Show All Tickets */}
<div className="d-flex align-items-center gap-2 pl-4">
  <Input
    type="checkbox"
    checked={showAll}
    onChange={() => setShowAll(!showAll)}
  />
  <span className="mb-0">Show All Tickets</span>
</div>

{/* Instructions For */}
<div className="d-flex align-items-center gap-2 pl-4">
  <Label for="instructionsFilter" className="mb-0">Instructions For:</Label>
  <Input
    id="instructionsFilter"
    type="select"
    value={instructionsForFilter}
    onChange={(e) => setInstructionsForFilter(e.target.value)}
    style={{ minWidth: "180px" }}
  >
    <option value="">All</option>
    {[...new Set(
      Object.values(projectTickets)
        .flatMap((p) => p.tickets.map((t) => t.instructionsFor))
        .filter(Boolean)
    )].map((instr) => (
      <option key={instr} value={instr}>{instr}</option>
    ))}
  </Input>
</div>

{/* Expand All */}
<div className="d-flex align-items-center gap-2 pl-4">
  <Input
    type="checkbox"
    checked={allExpanded}
    onChange={toggleExpandAll}
  />
  <span className="mb-0">Expand All</span>
</div>

</div>
  );

  return (
    <Container className="mt-4" fluid>
      <Row>
        <Col md="12">
          <Card className="shadow">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Tickets Overview</h5>
              <Button
                color="primary"
                onClick={() => {
                  setEditTicket(null);
                  setModalOpen(true);
                }}
              >
                Add Ticket
              </Button>
            </CardHeader>
            <CardBody>
              {error && <div className="alert alert-danger" role="alert">{error}</div>}
              {renderControlPanel()}

              {Object.entries(projectTickets).map(([projectId, project]) => {
                let displayedTickets = project.tickets;

                if (!showAll) {
                  displayedTickets = displayedTickets.filter((t) => !t.checked);
                }

                if (instructionsForFilter) {
                  displayedTickets = displayedTickets.filter(
                    (t) => t.instructionsFor === instructionsForFilter
                  );
                }

                if (displayedTickets.length === 0) return null;

                const isExpanded = expandedProjects[projectId] || false;
                const totalTickets = project.tickets.length;
                const uncheckedTickets = project.tickets.filter((t) => !t.checked).length;

                return (
                  <Card key={projectId} className="mb-3">
                    <CardHeader className="d-flex justify-content-between align-items-center">
                      <strong>
                        <Link to={`/admin/projects/${projectId}`}>
                          {project.projectName}
                        </Link> ({uncheckedTickets} Open Tickets / {totalTickets} Total Tickets)
                      </strong>
                      <Button size="sm" onClick={() => handleToggleExpand(projectId)}>
                        {isExpanded ? "Hide Tickets" : "Show Tickets"}
                      </Button>
                    </CardHeader>
                    {isExpanded && (
                      <CardBody>
                        <Table responsive striped>
                          <thead>
                            <tr>
                              <th>Check</th>
                              <th>Ticket Name</th>
                              <th>Created By</th>
                              <th>Created At</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedTickets.map((ticket) => (
                              <tr key={ticket.ticketId}>
                                <td>
                                  <Input
                                    type="checkbox"
                                    checked={!!ticket.checked}
                                    onChange={() => handleToggleChecked(ticket)}
                                  />
                                </td>
                                <td>{ticket.name || "N/A"}</td>
                                <td>{ticket.createdBy || "N/A"}</td>
                                <td>{ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : "N/A"}</td>
                                <td>
                                  <Button color="warning" size="sm" onClick={() => handleEditTicket(ticket)} className="me-2">Edit</Button>
                                  <Button color="danger" size="sm" onClick={() => handleDeleteTicket(ticket)}>Delete</Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </CardBody>
                    )}
                  </Card>
                );
              })}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ProjectTicketModal
        projectId={editTicket ? editTicket.projectId : null}
        openModal={modalOpen}
        setModalOpen={setModalOpen}
        setError={setError}
        editTicket={editTicket}
      />
    </Container>
  );
};

export default TicketsOverview;
